// DetectionBySeverityV2.jsx
import React, { useMemo } from "react";
import Chart from "react-apexcharts";

import { ReactComponent as ArrowUpIcon } from "../../../../../vendor/_new/icon/overview/arrow_up.svg";
import { ReactComponent as ArrowDownIcon } from "../../../../../vendor/_new/icon/overview/arrow_down.svg";
import colors from "../../../../../utils/colors";
import { withApexChartFont } from "../../../../../utils/apexChartDefaults";
import { getLabelForLatestDetections } from "..";

import { Stack, Typography } from "@mui/material";
import useHome from "../../../../../hooks/user/useHome";
import { formatPercentChange } from "../../../../../utils/functions";
import { detectionPercentColor, isDetectionTrendUp } from "../overviewPercentUtils";

const SEVERITY_KEY_BY_LABEL = {
  Critical: "critical",
  High: "high",
  Medium: "medium",
  Low: "low",
};

const DetectionBySeverityV2 = ({
  label = "Critical",
  timeseriesSource = "hostedscan",
  percentChange = null,
}) => {
  const { hostedscan_data, detectionsSeverityTimeseries } = useHome();

  const series = useMemo(() => {
    if (timeseriesSource === "waf") {
      const severityKey = SEVERITY_KEY_BY_LABEL[label] || "critical";
      const ts = detectionsSeverityTimeseries?.[severityKey] || [];
      return [
        {
          name: `Runtime detections (${label})`,
          data: ts.map((item) => {
            const t = item.key_as_string
              ? new Date(item.key_as_string).getTime()
              : item.key
              ? new Date(item.key).getTime()
              : 0;
            return [t, item.doc_count ?? item.value ?? 0];
          }),
        },
      ];
    }
    const data = label === "Critical" ? hostedscan_data?.critical : label === "High" ? hostedscan_data?.high : hostedscan_data?.medium;
    return [
      {
        name: `Detection by Severity for ${label}`,
        data: data?.map((item) => [new Date(item?.key).getTime(), item?.value]),
      },
    ];
  }, [label, hostedscan_data, detectionsSeverityTimeseries, timeseriesSource]);

  const displayPercent = percentChange;

  const options = withApexChartFont({
    chart: {
      type: "area",
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true },
      sparkline: { enabled: false },
      foreColor: "#696969",
    },
    dataLabels: {
      enabled: false,
    },
    colors: [label === "Critical" ? "rgba(242, 137, 137, 1)" : "rgba(255, 219, 67, 1)"],
    stroke: {
      curve: "smooth",
      width: 1,
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 1,
        inverseColors: false,
        gradientToColors: [label === "Critical" ? "rgba(242, 137, 137, 0.25)" : "rgba(255, 219, 67, 0.25)"],
        opacityFrom: 1,
        opacityTo: 0,
        stops: [0, 100],
      },
    },
    markers: {
      size: 0,
    },
    xaxis: {
      labels: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      show: false,
    },
    grid: {
      show: false,
    },
    tooltip: {
      enabled: false,
      shared: false,
      followCursor: false,
    },
    legend: { show: false },
  });

  return (
    <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={2} sx={{ height: 40, px: 3 }}>
      {getLabelForLatestDetections(label)}
      <Chart options={options} series={series} type="area" height={50} />
      <Stack direction={"row"} alignItems={"center"} justifyContent={"flex-end"} sx={{ width: 70 }}>
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: "21px",
            color: detectionPercentColor(displayPercent),
          }}
        >
          {displayPercent === null && timeseriesSource === "hostedscan" ? "—" : formatPercentChange(displayPercent)}
        </Typography>
        {displayPercent === null && timeseriesSource === "hostedscan" ? null : isDetectionTrendUp(displayPercent) ? (
          <ArrowUpIcon style={{ color: colors.red[100], width: 20, height: 20 }} />
        ) : displayPercent === 0 ? null : (
          <ArrowDownIcon style={{ color: "#1DD75B", width: 20, height: 20 }} />
        )}
      </Stack>
    </Stack>
  );
};
export default DetectionBySeverityV2;
